import { useState, useMemo, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { isDigitalFormat, effectiveShipPrice, effectiveShipTimes, deliveryRangeText } from "@/lib/shipping";
import { trackEvent, gaItem } from "@/lib/analytics";

const REQUIRED_CONTACT = ["first_name", "last_name", "email", "mobile"];
const REQUIRED_ADDRESS = ["country", "full_name", "line1", "city", "state", "postal_code"];

const emptyContact = { first_name: "", last_name: "", email: "", mobile: "", company: "", alt_phone: "" };
const emptyAddress = { country: "United States", full_name: "", line1: "", line2: "", apartment: "", city: "", state: "", postal_code: "", landmark: "" };

function Field({ label, required, testId, children, className = "" }) {
  return (
    <div className={className}>
      <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60" htmlFor={testId}>
        {label}{required && <span className="text-[#7A2021]"> *</span>}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function AddressForm({ prefix, address, setAddress }) {
  const set = (k, v) => setAddress({ ...address, [k]: v });
  return (
    <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
      <Field label="Country / Region" required testId={`${prefix}-country`} className="md:col-span-3">
        <Input id={`${prefix}-country`} data-testid={`${prefix}-country`} required value="United States" readOnly disabled className="rounded-md border-slate-300 h-10 bg-slate-100 cursor-not-allowed" />
        <p className="text-[11px] text-slate-500 mt-1">We currently ship only within the United States.</p>
      </Field>
      <Field label="Full name" required testId={`${prefix}-full-name`} className="md:col-span-3">
        <Input id={`${prefix}-full-name`} data-testid={`${prefix}-full-name`} required value={address.full_name} onChange={(e) => set("full_name", e.target.value)} className="rounded-none border-[#E5E1D8] h-11" />
      </Field>
      <Field label="Street address, line 1" required testId={`${prefix}-line1`} className="md:col-span-6">
        <Input id={`${prefix}-line1`} data-testid={`${prefix}-line1`} required value={address.line1} onChange={(e) => set("line1", e.target.value)} className="rounded-none border-[#E5E1D8] h-11" />
      </Field>
      <Field label="Street address, line 2 (optional)" testId={`${prefix}-line2`} className="md:col-span-3">
        <Input id={`${prefix}-line2`} data-testid={`${prefix}-line2`} value={address.line2} onChange={(e) => set("line2", e.target.value)} className="rounded-none border-[#E5E1D8] h-11" />
      </Field>
      <Field label="Apartment / Suite / Unit (optional)" testId={`${prefix}-apartment`} className="md:col-span-3">
        <Input id={`${prefix}-apartment`} data-testid={`${prefix}-apartment`} value={address.apartment} onChange={(e) => set("apartment", e.target.value)} className="rounded-none border-[#E5E1D8] h-11" />
      </Field>
      <Field label="City" required testId={`${prefix}-city`} className="md:col-span-2">
        <Input id={`${prefix}-city`} data-testid={`${prefix}-city`} required value={address.city} onChange={(e) => set("city", e.target.value)} className="rounded-none border-[#E5E1D8] h-11" />
      </Field>
      <Field label="State / Province" required testId={`${prefix}-state`} className="md:col-span-2">
        <Input id={`${prefix}-state`} data-testid={`${prefix}-state`} required value={address.state} onChange={(e) => set("state", e.target.value)} className="rounded-none border-[#E5E1D8] h-11" />
      </Field>
      <Field label="ZIP / Postal code" required testId={`${prefix}-postal`} className="md:col-span-2">
        <Input id={`${prefix}-postal`} data-testid={`${prefix}-postal`} required value={address.postal_code} onChange={(e) => set("postal_code", e.target.value)} className="rounded-none border-[#E5E1D8] h-11" />
      </Field>
      <Field label="Landmark (optional)" testId={`${prefix}-landmark`} className="md:col-span-6">
        <Input id={`${prefix}-landmark`} data-testid={`${prefix}-landmark`} value={address.landmark} onChange={(e) => set("landmark", e.target.value)} className="rounded-none border-[#E5E1D8] h-11" />
      </Field>
    </div>
  );
}

export default function Checkout() {
  const { items, totals, revalidate, shippingSettings } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  const physicalItems = items.filter((i) => !isDigitalFormat(i.format));
  const hasPhysical = physicalItems.length > 0;
  // Estimate uses per-book overrides when present; the slowest item wins
  // (mirrors the shipping_estimate stored on the order server-side).
  const orderDeliveryRange = hasPhysical
    ? (() => {
        const times = physicalItems.map((i) => effectiveShipTimes(i, shippingSettings));
        return deliveryRangeText(
          Math.max(...times.map((t) => t.handling)),
          Math.max(...times.map((t) => t.min)),
          Math.max(...times.map((t) => t.max)),
        );
      })()
    : null;

  const [contact, setContact] = useState(() => ({
    ...emptyContact,
    email: user?.email || "",
    first_name: (user?.name || "").split(" ")[0] || "",
    last_name: (user?.name || "").split(" ").slice(1).join(" "),
  }));
  const [shipping, setShipping] = useState(emptyAddress);
  const [billing, setBilling] = useState(emptyAddress);
  const [billingSame, setBillingSame] = useState(true);
  const [loading, setLoading] = useState(false);

  // GA4: fire begin_checkout once per checkout entry (guard survives StrictMode)
  const bcFired = useRef(false);
  useEffect(() => {
    if (bcFired.current || items.length === 0) return;
    bcFired.current = true;
    trackEvent("begin_checkout", {
      currency: "USD",
      value: Number(totals.total.toFixed(2)),
      items: items.map(gaItem),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contactValid = useMemo(() => REQUIRED_CONTACT.every((k) => (contact[k] || "").trim().length > 0), [contact]);
  const shippingValid = useMemo(() => REQUIRED_ADDRESS.every((k) => (shipping[k] || "").trim().length > 0), [shipping]);
  const billingValid = billingSame || REQUIRED_ADDRESS.every((k) => (billing[k] || "").trim().length > 0);
  const formValid = contactValid && shippingValid && billingValid && items.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!formValid) {
      toast.error("Please complete every required field.");
      return;
    }
    setLoading(true);
    try {
      // Revalidate stock immediately before creating the Stripe session.
      const val = await revalidate();
      if (!val.all_ok) {
        toast.error("Some items were adjusted. Please review your cart before continuing.");
        setLoading(false);
        nav("/");
        return;
      }
      const { data } = await api.post("/checkout/session", {
        items: items.map((i) => ({
          book_id: i.book_id, quantity: i.quantity, format: i.format,
        })),
        origin_url: window.location.origin,
        contact,
        shipping_address: shipping,
        billing_same_as_shipping: billingSame,
        billing_address: billingSame ? null : billing,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Checkout could not start");
    } finally {
      setLoading(false);
    }
  };

  const setContactField = (k, v) => setContact({ ...contact, [k]: v });

  if (items.length === 0) {
    return (
      <div className="pt-28 md:pt-40 min-h-screen max-w-[900px] mx-auto px-6 md:px-10 pb-24" data-testid="checkout-empty">
        <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Checkout</p>
        <h1 className="font-serif text-6xl md:text-8xl tracking-tight leading-none text-[#1A1714] mt-3">
          Your basket is <em className="font-light">quiet.</em>
        </h1>
        <Link to="/shop" className="mt-8 inline-block border border-[#1A1714] px-6 py-3 font-mono text-[11px] uppercase tracking-editorial hover:bg-[#1A1714] hover:text-[#F9F6F0] transition-colors">
          Browse the shelf →
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="pt-24 md:pt-32 pb-16 bg-[#F9F6F0] min-h-screen" data-testid="checkout-page">
      <div className="max-w-[1200px] mx-auto px-6">
        <Link to="/books" className="inline-flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900">
          <ArrowLeft size={14} /> Continue shopping
        </Link>
        <h1 className="text-3xl md:text-4xl font-semibold text-slate-900 mt-4 mb-2" data-testid="checkout-title">
          Checkout
        </h1>
        <p className="text-sm text-slate-500 mb-8">
          Complete your order in three quick steps. Payment is processed securely by Stripe.
        </p>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-8">
          {/* Step 1 — Customer information */}
          <section data-testid="checkout-step-contact" className="bg-white border border-slate-200 p-6 rounded-lg">
            <div className="flex items-baseline gap-3 mb-5">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-semibold">1</span>
              <h2 className="text-xl font-medium text-slate-900">Customer information</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <Field label="First name" required testId="contact-first-name" className="md:col-span-3">
                <Input id="contact-first-name" data-testid="contact-first-name" required value={contact.first_name} onChange={(e) => setContactField("first_name", e.target.value)} className="rounded-md border-slate-300 h-10" />
              </Field>
              <Field label="Last name" required testId="contact-last-name" className="md:col-span-3">
                <Input id="contact-last-name" data-testid="contact-last-name" required value={contact.last_name} onChange={(e) => setContactField("last_name", e.target.value)} className="rounded-md border-slate-300 h-10" />
              </Field>
              <Field label="Company (optional)" testId="contact-company" className="md:col-span-6">
                <Input id="contact-company" data-testid="contact-company" value={contact.company} onChange={(e) => setContactField("company", e.target.value)} className="rounded-md border-slate-300 h-10" />
              </Field>
              <Field label="Email address" required testId="contact-email" className="md:col-span-3">
                <Input id="contact-email" data-testid="contact-email" type="email" required value={contact.email} onChange={(e) => setContactField("email", e.target.value)} className="rounded-md border-slate-300 h-10" />
              </Field>
              <Field label="Mobile number" required testId="contact-mobile" className="md:col-span-3">
                <Input id="contact-mobile" data-testid="contact-mobile" type="tel" required value={contact.mobile} onChange={(e) => setContactField("mobile", e.target.value)} className="rounded-md border-slate-300 h-10" />
              </Field>
              <Field label="Alternate phone (optional)" testId="contact-alt-phone" className="md:col-span-3">
                <Input id="contact-alt-phone" data-testid="contact-alt-phone" type="tel" value={contact.alt_phone} onChange={(e) => setContactField("alt_phone", e.target.value)} className="rounded-md border-slate-300 h-10" />
              </Field>
            </div>
          </section>

          {/* Step 2 — Shipping address */}
          <section data-testid="checkout-step-shipping" className="bg-white border border-slate-200 p-6 rounded-lg">
            <div className="flex items-baseline gap-3 mb-5">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-semibold">2</span>
              <h2 className="text-xl font-medium text-slate-900">Shipping address</h2>
            </div>
            <AddressForm prefix="shipping" address={shipping} setAddress={setShipping} />
          </section>

          {/* Step 3 — Billing address */}
          <section data-testid="checkout-step-billing" className="bg-white border border-slate-200 p-6 rounded-lg">
            <div className="flex items-baseline gap-3 mb-5">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-semibold">3</span>
              <h2 className="text-xl font-medium text-slate-900">Billing address</h2>
            </div>
            <label className="flex items-center gap-3 cursor-pointer" data-testid="checkout-billing-same-label">
              <Checkbox
                checked={billingSame}
                onCheckedChange={(v) => setBillingSame(!!v)}
                data-testid="checkout-billing-same"
              />
              <span className="text-sm text-slate-800">Billing address is the same as shipping address</span>
            </label>
            {!billingSame && (
              <div className="mt-6" data-testid="checkout-billing-form">
                <AddressForm prefix="billing" address={billing} setAddress={setBilling} />
              </div>
            )}
          </section>
        </div>

        {/* Order summary */}
        <aside className="lg:col-span-4 lg:sticky lg:top-24 h-fit bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="checkout-summary">
          <h3 className="text-lg font-medium text-slate-900">Order summary</h3>
          <div className="space-y-3 max-h-72 overflow-y-auto -mx-1 px-1">
            {items.map((i) => (
              <div key={`${i.book_id}-${i.format}`} className="flex gap-3">
                <div className="w-12 h-16 flex-shrink-0 bg-slate-100 rounded overflow-hidden">
                  {i.cover_image && <img src={i.cover_image} alt={i.title} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{i.title}</p>
                  <p className="text-xs text-slate-500 capitalize">{i.format} · Qty {i.quantity}</p>
                  {isDigitalFormat(i.format) ? (
                    <p className="text-xs text-slate-400">Digital delivery — no shipping</p>
                  ) : shippingSettings.mode === "flat" && i.shipping_price == null ? (
                    <p className="text-xs text-slate-400">Covered by flat per-order rate</p>
                  ) : effectiveShipPrice(i, shippingSettings) > 0 ? (
                    <p className="text-xs text-slate-400">Shipping ${effectiveShipPrice(i, shippingSettings).toFixed(2)} × {i.quantity}</p>
                  ) : (
                    <p className="text-xs text-emerald-700">Free shipping</p>
                  )}
                </div>
                <p className="text-sm text-slate-900 whitespace-nowrap font-medium">${(i.price * i.quantity).toFixed(2)}</p>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-200 pt-4 space-y-2 text-sm">
            <div className="flex justify-between text-slate-700">
              <span>Subtotal</span>
              <span data-testid="checkout-subtotal">${totals.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-slate-700">
              <span>Shipping</span>
              <span data-testid="checkout-shipping">
                {totals.shipping > 0 ? `$${totals.shipping.toFixed(2)}` : <span className="text-emerald-700 font-medium">Free</span>}
              </span>
            </div>
            {orderDeliveryRange && (
              <p className="text-xs text-slate-500" data-testid="checkout-delivery-estimate">
                Estimated delivery <strong className="text-slate-700">{orderDeliveryRange}</strong>
                {shippingSettings.mode === "flat" ? " · one flat rate per order" : ""}
              </p>
            )}
            <div className="flex justify-between text-base font-semibold text-slate-900 border-t border-slate-200 pt-2 mt-2">
              <span>Total</span>
              <span data-testid="checkout-total">${totals.total.toFixed(2)}</span>
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading || !formValid}
            data-testid="checkout-submit"
            className="w-full rounded-md bg-slate-900 text-white hover:bg-slate-800 text-sm font-semibold h-11 disabled:opacity-50"
          >
            {loading ? "Preparing Stripe…" : "Continue to payment"}
          </Button>
          <p className="text-xs text-slate-500 text-center">
            Payment is completed securely by Stripe. Your card details are never stored on our servers.
          </p>
        </aside>
      </div>
    </form>
  );
}
